import {
  getPriceForPart,
  isUnpricedByDesign,
  PAD_STYLE_LABELS,
} from "./getPriceForPart";
import { isArticleFreePart, isPartHiddenFromPlan } from "./partVisibility";
import { countTotalRivets } from "./rivets";
import {
  type AdditionalItemPosition,
  type MaterialInfo,
  type MaterialPosition,
  MaterialType,
  type PartLike,
  type PriceCalcOptions,
  type PriceCalculation,
  type PriceCalculationIssue,
  type PriceCalculationIssueGrouped,
  type PriceCalculationPartRef,
  type PriceData,
  type PriceParameters,
  PriceUnit,
} from "./types";

/**
 * Thrown by {@link calculatePrice} with `onMissingPrice: "throw"` once every part
 * has been checked, when at least one article has no price entry. `issues` holds
 * every issue of the calculation, grouped as in `PriceCalculation.priceIssues`.
 */
export class PriceIssuesError extends Error {
  public readonly issues: PriceCalculationIssueGrouped[];

  public constructor(issues: PriceCalculationIssueGrouped[]) {
    const codes = issues
      .filter((issue) => issue.kind === "missing_db_entry")
      .map((issue) => issue.code);
    super(`No price entry for code ${[...new Set(codes)].join(", ")}`);
    this.name = "PriceIssuesError";
    this.issues = issues;
  }
}

function partRefOf(part: PartLike): Partial<PriceCalculationPartRef> {
  const props = part.properties ?? part;
  const optionUuid =
    sourceOptionUuidOf(part.dragConstraint) ??
    sourceOptionUuidOf(props.dragConstraint);
  return {
    partId: nonEmptyString(part.id) ?? nonEmptyString(props.id),
    optionUuid,
  };
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

function sourceOptionUuidOf(dragConstraint: unknown): string | undefined {
  if (typeof dragConstraint !== "object" || dragConstraint === null) {
    return undefined;
  }
  return nonEmptyString(
    (dragConstraint as { sourceOptionUuid?: unknown }).sourceOptionUuid,
  );
}

function partRefOfIssue(
  issue: PriceCalculationIssue,
): PriceCalculationPartRef | undefined {
  if (!issue.partId) return undefined;
  return issue.optionUuid
    ? { partId: issue.partId, optionUuid: issue.optionUuid }
    : { partId: issue.partId };
}

function aggregatePriceIssues(
  raw: PriceCalculationIssue[],
): PriceCalculationIssueGrouped[] {
  const map = new Map<string, PriceCalculationIssueGrouped>();
  for (const issue of raw) {
    const key = `${issue.kind}\0${issue.code ?? ""}\0${issue.detail}`;
    const partRef = partRefOfIssue(issue);
    const prev = map.get(key);
    if (prev) {
      prev.count += 1;
      if (partRef) prev.parts.push(partRef);
    } else
      map.set(key, {
        count: 1,
        kind: issue.kind,
        code: issue.code,
        detail: issue.detail,
        parts: partRef ? [partRef] : [],
      });
  }
  return [...map.values()].sort(
    (a, b) => b.count - a.count || a.detail.localeCompare(b.detail, "de"),
  );
}

function panelKindLabelForIssue(props: PartLike): string {
  if (props.type === "TrayPanel") return "Tableau";
  if (props.type !== "Panel") return "Platte";
  const panel = props.panelType;
  if (typeof panel === "number") {
    return (
      ["Außenplatte", "Tableau", "Rollbrett", "Unterlage", "Schienen"][panel] ??
      "Platte"
    );
  }
  return "Platte";
}

/** Bezeichnung für fehlenden DB-Eintrag (gruppierbar pro Farbe/Typ). */
function detailForMissingDbEntry(props: PartLike, info: MaterialInfo): string {
  const color = props.color as
    | { name?: string; description?: string }
    | undefined;
  if (props.type === "Panel" || props.type === "TrayPanel") {
    const kind = panelKindLabelForIssue(props);
    const colorName = color?.name ?? color?.description ?? "";
    if (colorName) return `${kind} ${colorName}`;
    return `${kind} (${info.code})`;
  }
  return info.description || info.code;
}

/** A foam pad without an article: "Schaumstoff (Geschlitzt), 20 mm". */
function padLabel(props: PartLike): string {
  const base = props.partSelection?.description || "Schaumstoff";
  const style = props.style ?? props.partSelection?.style;
  const styleLabel =
    typeof style === "string" ? PAD_STYLE_LABELS[style] : undefined;
  const strength = props.strength ?? props.partSelection?.strength;
  const styled = styleLabel ? `${base} (${styleLabel})` : base;
  return typeof strength === "number"
    ? `${styled}, ${strength.toLocaleString("de-DE")} mm`
    : styled;
}

/** Bezeichnung eines Teils, für das kein Artikel ermittelt werden konnte. */
export function partLabelForMissingCode(props: PartLike): string {
  // A configured pad carries a part selection; without an article name it is
  // labelled by its foam, whatever else the selection holds.
  if (props.type === "Pad" && props.partSelection && !props.partSelection.name)
    return padLabel(props);
  const t = props.type ?? "?";
  const name =
    props.partSelection?.name ??
    props.partSelection?.description ??
    props.color?.name ??
    props.material?.name;
  const strength =
    typeof props.strength === "number" ? `, ${props.strength} mm` : "";
  if (props.type === "Panel" || props.type === "TrayPanel") {
    const panel = props.panelType;
    const panelHint =
      typeof panel === "number"
        ? (["Außenplatte", "Tableau", "Rollbrett", "Unterlage", "Schienen"][
            panel
          ] ?? `Panel-Typ ${panel}`)
        : "";
    return name
      ? `${t} (${panelHint || "Platte"}): ${name}${strength}`
      : `${t} (${panelHint || "Platte"})${strength}`;
  }
  return name ? `${t}: ${name}` : t;
}

const DEFAULT_RIVET_ARTICLE_CODE = "12345";
const DEFAULT_RIVET_PRICE_DIVISOR = 1000;
const DEFAULT_VAT_RATE = 0.19;

function normalizePartList(
  partList: Iterable<PartLike> | Map<string, PartLike> | null | undefined,
): PartLike[] {
  const isIterable = (value: unknown): value is Iterable<PartLike> =>
    value != null &&
    typeof (value as { [Symbol.iterator]?: unknown })[Symbol.iterator] ===
      "function";

  if (partList instanceof Map) {
    return Array.from(partList.values());
  }

  if (Array.isArray(partList)) {
    return partList;
  }

  if (partList && typeof partList === "object") {
    const values = (partList as { values?: () => Iterable<PartLike> }).values;
    if (typeof values === "function") {
      const valueList = values.call(partList);
      return isIterable(valueList) ? Array.from(valueList) : [];
    }
  }

  return isIterable(partList) ? Array.from(partList) : [];
}

function sumBy<T>(items: T[], selector: (item: T) => number): number {
  return items.reduce((acc, item) => acc + selector(item), 0);
}

/** Logs a missing price entry for "warn"; "throw" collects and throws after the loop. */
function handleMissingPrice(
  code: string,
  behavior: PriceCalcOptions["onMissingPrice"],
): void {
  if (behavior === "throw" || behavior === "ignore") return;
  console.error(`No price entry for code ${code}`);
}

export function calculatePrice(
  partList: Iterable<PartLike> | Map<string, PartLike>,
  priceData: PriceData,
  parameters: PriceParameters,
  options: PriceCalcOptions = {},
): PriceCalculation | null {
  if (!priceData || Object.keys(priceData).length === 0) {
    return null;
  }

  const parts = normalizePartList(partList);
  const material: MaterialPosition[] = [];
  const priceIssues: PriceCalculationIssue[] = [];
  const { onMissingPrice = "warn" } = options;

  const rivetArticleCode =
    options.rivetArticleCode ?? DEFAULT_RIVET_ARTICLE_CODE;
  const rivetPriceDivisor =
    options.rivetPriceDivisor ?? DEFAULT_RIVET_PRICE_DIVISOR;
  const vatRate = options.vatRate ?? DEFAULT_VAT_RATE;
  const additionalItems: AdditionalItemPosition[] = (
    options.additionalItems ?? []
  )
    .filter((item) => item.count > 0)
    .map((item) => {
      const materialTotal = item.purchasePrice * item.count;
      const workCost = (item.workUnits * item.count * parameters.workCost) / 60;
      return {
        ...item,
        materialTotal,
        workCost,
        total: materialTotal + workCost,
      };
    });

  const rivets = countTotalRivets(parts, priceData);

  const rivetEntry = priceData[rivetArticleCode];
  if (!rivetEntry) {
    handleMissingPrice(rivetArticleCode, onMissingPrice);
    priceIssues.push({
      kind: "missing_db_entry",
      code: rivetArticleCode,
      detail: "Nieten",
    });
  }

  const rivetPrice = rivetEntry
    ? rivetEntry.pricePurchase / rivetPriceDivisor
    : 0;

  for (const part of parts) {
    if (isPartHiddenFromPlan(part)) continue;

    const props = part.properties ?? part;

    const info = getPriceForPart(props);
    if (!info) {
      if (!isArticleFreePart(props) && !isUnpricedByDesign(props)) {
        priceIssues.push({
          kind: "missing_material_code",
          detail: partLabelForMissingCode(props),
          ...partRefOf(part),
        });
      }
      continue;
    }

    const entry = priceData[info.code];
    if (!entry) {
      handleMissingPrice(info.code, onMissingPrice);
      priceIssues.push({
        kind: "missing_db_entry",
        code: info.code,
        detail: detailForMissingDbEntry(props, info),
        ...partRefOf(part),
      });
      continue;
    }

    const work = entry.workingTimeMinutes
      ? entry.workingTimeMinutes * info.amount
      : 0;

    material.push({
      ...info,
      price: entry.pricePurchase,
      total: entry.pricePurchase * info.amount,
      work,
    });
  }

  const groupedIssues = aggregatePriceIssues(priceIssues);
  if (
    onMissingPrice === "throw" &&
    groupedIssues.some((issue) => issue.kind === "missing_db_entry")
  ) {
    throw new PriceIssuesError(groupedIssues);
  }

  material.push({
    amount: rivets,
    code: rivetArticleCode,
    description: "Nieten",
    price: rivetPrice,
    total: rivets * rivetPrice,
    type: MaterialType.Other,
    unit: PriceUnit.Piece,
    work: 0,
  });

  const sumMaterial = sumBy(material, (item) => item.total);
  const wastedPanel =
    sumBy(
      material.filter((item) => item.type === MaterialType.Panel),
      (item) => item.total,
    ) * parameters.pPanel;
  const wastedAluminium =
    sumBy(
      material.filter((item) => item.type === MaterialType.Alu),
      (item) => item.total,
    ) * parameters.pAlu;
  const procurementCost =
    (sumMaterial + wastedAluminium + wastedPanel) * parameters.pNKMat;
  const materialTotal =
    procurementCost + sumMaterial + wastedAluminium + wastedPanel;

  const amountMarginMaterial = materialTotal * parameters.pMarginMaterial;
  const workTimeInHours =
    (sumBy(material, (item) => item.work) / 60) * (1 + parameters.pNebenzeit);
  const additionalCost =
    workTimeInHours * parameters.workCost + parameters.otherCost;
  const intermediate1 = materialTotal + additionalCost + amountMarginMaterial;
  const amountMarginTotal = intermediate1 * parameters.pMarginTotal;
  const extraTime = parameters.pNebenzeit * workTimeInHours;
  const additionalItemsMaterial = sumBy(
    additionalItems,
    (item) => item.materialTotal,
  );
  const additionalItemsWorkCost = sumBy(
    additionalItems,
    (item) => item.workCost,
  );
  const additionalItemsTotal =
    additionalItemsMaterial + additionalItemsWorkCost;
  const priceNetto =
    intermediate1 * (1 + parameters.pMarginTotal) + additionalItemsTotal;
  const priceBrutto = priceNetto * (1 + vatRate);

  return {
    additionalItems,
    additionalCost,
    additionalItemsMaterial,
    additionalItemsTotal,
    additionalItemsWorkCost,
    amountMarginMaterial,
    amountMarginTotal,
    extraTime,
    material,
    materialTotal,
    priceBrutto,
    priceNetto,
    priceIssues: groupedIssues,
    procurementCost,
    sumMaterial,
    wastedAluminium,
    wastedPanel,
    workTime: workTimeInHours,
  };
}
